'use client';

import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'utils/cn';
import { useOverlayScope } from 'components/ui/overlay-motion';

function Tabs({ className, orientation = 'horizontal', ...props }: TabsPrimitive.Root.Props) {
    return (
        <TabsPrimitive.Root
            data-slot="tabs"
            orientation={orientation}
            data-orientation={orientation}
            className={cn('group/tabs flex gap-2 data-horizontal:flex-col', className)}
            {...props}
        />
    );
}

const tabsListVariants = cva(
    'group/tabs-list text-muted-foreground relative inline-flex w-fit items-center justify-center rounded-lg p-[3px] group-data-horizontal/tabs:h-9 group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col data-[variant=line]:rounded-none',
    {
        variants: {
            variant: {
                default: 'bg-muted new:bg-segmented new:gap-1 new:rounded-[9px] new:group-data-horizontal/tabs:h-8',
                line: 'gap-1 bg-transparent',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    }
);

/**
 * Where the new motion applies the active tab is marked by one indicator that slides to the chosen tab: the
 * segmented list's pill, or the line list's underline, the strip's primary 2px under the tab. It sits under
 * the tabs, which draw no mark of their own there.
 */
const TAB_INDICATOR = {
    default: 'top-(--active-tab-top) h-(--active-tab-height) rounded-sm bg-card shadow-segment',
    line: 'top-[calc(var(--active-tab-top)+var(--active-tab-height))] h-0.5 bg-primary data-vertical:hidden!',
};

function TabsList({
    className,
    variant = 'default',
    children,
    ...props
}: TabsPrimitive.List.Props & VariantProps<typeof tabsListVariants>) {
    const { motion } = useOverlayScope();
    return (
        <TabsPrimitive.List
            data-slot="tabs-list"
            data-variant={variant}
            className={cn(tabsListVariants({ variant }), className)}
            {...props}
        >
            <TabsPrimitive.Indicator
                data-slot="tabs-indicator"
                className={cn(
                    'pointer-events-none absolute left-0 w-(--active-tab-width) translate-x-(--active-tab-left)',
                    TAB_INDICATOR[variant ?? 'default'],
                    motion.tabIndicator
                )}
            />
            {children}
        </TabsPrimitive.List>
    );
}

function TabsTrigger({ className, ...props }: TabsPrimitive.Tab.Props) {
    const { motion } = useOverlayScope();
    return (
        <TabsPrimitive.Tab
            data-slot="tabs-trigger"
            className={cn(
                "text-foreground/60 hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:text-muted-foreground dark:hover:text-foreground new:[&_svg[stroke]]:stroke-2 new:select-none relative inline-flex h-[calc(100%-1px)] flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,background-color] duration-150 group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-disabled:pointer-events-none aria-disabled:opacity-50 group-data-[variant=default]/tabs-list:data-active:shadow-sm group-data-[variant=line]/tabs-list:data-active:shadow-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                'group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-active:bg-transparent dark:group-data-[variant=line]/tabs-list:data-active:border-transparent dark:group-data-[variant=line]/tabs-list:data-active:bg-transparent',
                'data-active:bg-background data-active:text-foreground dark:data-active:border-input dark:data-active:bg-input/30 dark:data-active:text-foreground',
                'new:group-data-[variant=default]/tabs-list:text-foreground-tertiary new:group-data-[variant=default]/tabs-list:rounded-sm new:group-data-[variant=default]/tabs-list:px-2.5 new:group-data-[variant=default]/tabs-list:text-meta new:group-data-[variant=default]/tabs-list:data-active:bg-transparent new:group-data-[variant=default]/tabs-list:data-active:text-foreground new:group-data-[variant=default]/tabs-list:data-active:shadow-none',
                'after:bg-foreground new:group-data-horizontal/tabs:group-data-[variant=line]/tabs-list:data-active:after:opacity-0 after:absolute after:opacity-0 after:transition-opacity group-data-horizontal/tabs:after:inset-x-0 group-data-horizontal/tabs:after:bottom-[-5px] group-data-horizontal/tabs:after:h-0.5 group-data-vertical/tabs:after:inset-y-0 group-data-vertical/tabs:after:-right-1 group-data-vertical/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-active:after:opacity-100',
                motion.tab,
                className
            )}
            {...props}
        />
    );
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
    return (
        <TabsPrimitive.Panel
            data-slot="tabs-content"
            className={cn(
                'focus-visible:ring-ring/50 new:focus-visible:outline-ring new:focus-visible:ring-0 new:focus-visible:outline-2 new:focus-visible:-outline-offset-2 new:focus-visible:outline-solid flex-1 text-sm outline-none focus-visible:ring-[3px]',
                className
            )}
            {...props}
        />
    );
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants };
